/**
 * 各種コピー — 管理者向け一覧・追加・編集・削除（GET/POST/PUT/DELETE /api/various-copies）
 */
import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { isBuiltinVariousCopyDocCode } from '../../../../../catalog/variousCopyDocCodes';
import { API_BASE_URL } from '../../../../../config';
import type { VariousCopyRecord } from './types';
import './VariousCopiesUpload.css';

interface ApiVariousCopyRow {
  docCode: string;
  nameJa: string;
  nameEn?: string | null;
  nameVi?: string | null;
  descriptionJa?: string | null;
  descriptionEn?: string | null;
  descriptionVi?: string | null;
  builtin?: boolean;
}

function mapApiToRecord(row: ApiVariousCopyRow): VariousCopyRecord {
  return {
    docCode: row.docCode,
    nameJa: row.nameJa ?? '',
    nameEn: row.nameEn ?? '',
    nameVi: row.nameVi ?? '',
    descriptionJa: row.descriptionJa ?? '',
    descriptionEn: row.descriptionEn ?? '',
    descriptionVi: row.descriptionVi ?? '',
    builtin: isBuiltinVariousCopyDocCode(row.docCode) || row.builtin === true,
  };
}

function buildCreateBody(payload: FormState): Record<string, unknown> {
  return {
    nameJa: payload.nameJa.trim(),
    nameEn: payload.nameEn.trim() || null,
    nameVi: payload.nameVi.trim() || null,
    descriptionJa: payload.descriptionJa.trim() || null,
    descriptionEn: payload.descriptionEn.trim() || null,
    descriptionVi: payload.descriptionVi.trim() || null,
  };
}

async function parseErrorMessage(res: Response): Promise<string> {
  try {
    const data = await res.json();
    if (data && typeof data.message === 'string') return data.message;
  } catch {
    /* ignore */
  }
  if (res.status === 400) return '入力内容を確認してください（日本語の書類名は必須です）。';
  if (res.status === 404) return '対象のデータが見つかりません。';
  return `リクエストに失敗しました（${res.status}）。`;
}

type ModalMode = 'add' | 'edit';

interface FormState {
  nameJa: string;
  nameEn: string;
  nameVi: string;
  descriptionJa: string;
  descriptionEn: string;
  descriptionVi: string;
}

function recordToForm(r: VariousCopyRecord): FormState {
  return {
    nameJa: r.nameJa,
    nameEn: r.nameEn,
    nameVi: r.nameVi,
    descriptionJa: r.descriptionJa,
    descriptionEn: r.descriptionEn,
    descriptionVi: r.descriptionVi,
  };
}

function emptyForm(): FormState {
  return {
    nameJa: '',
    nameEn: '',
    nameVi: '',
    descriptionJa: '',
    descriptionEn: '',
    descriptionVi: '',
  };
}

const INITIAL_ADD_FORM = emptyForm();

interface VariousCopyModalProps {
  isOpen: boolean;
  mode: ModalMode;
  initial: FormState;
  onClose: () => void;
  onSave: (payload: FormState) => Promise<void>;
  onDelete?: () => Promise<void>;
}

const VariousCopyModal: React.FC<VariousCopyModalProps> = ({
  isOpen,
  mode,
  initial,
  onClose,
  onSave,
  onDelete,
}) => {
  const [form, setForm] = useState<FormState>(initial);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setForm(initial);
      setShowDeleteConfirm(false);
      setSubmitting(false);
    }
  }, [isOpen, initial]);

  if (!isOpen) return null;

  const title = mode === 'add' ? 'アップロード書類を追加' : 'アップロード書類を編集';
  const canSave = form.nameJa.trim().length > 0 && !submitting;

  const update = (key: keyof FormState, value: string) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleSave = async () => {
    if (!canSave || submitting) return;
    setSubmitting(true);
    try {
      await onSave({
        ...form,
        nameJa: form.nameJa.trim(),
        nameEn: form.nameEn.trim(),
        nameVi: form.nameVi.trim(),
        descriptionJa: form.descriptionJa.trim(),
        descriptionEn: form.descriptionEn.trim(),
        descriptionVi: form.descriptionVi.trim(),
      });
      onClose();
    } catch {
      /* 親が vc-upload__error に表示 */
    } finally {
      setSubmitting(false);
    }
  };

  const handleConfirmDelete = async () => {
    if (!onDelete || submitting) return;
    setSubmitting(true);
    try {
      await onDelete();
      onClose();
      setShowDeleteConfirm(false);
    } catch {
      /* 親が vc-upload__error に表示 */
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="vc-upload-modal-overlay" onClick={submitting ? undefined : onClose} role="dialog" aria-modal="true" aria-labelledby="vc-upload-modal-title">
      <div className="vc-upload-modal" onClick={(e) => e.stopPropagation()}>
        <div className="vc-upload-modal__header">
          <h2 id="vc-upload-modal-title" className="vc-upload-modal__title">
            {title}
          </h2>
          <button type="button" className="vc-upload-modal__close" onClick={onClose} aria-label="閉じる" disabled={submitting}>
            ×
          </button>
        </div>

        <div className="vc-upload-modal__body">
          <p className="vc-upload-modal__section-title">書類名</p>
          <div className="vc-upload-modal__field">
            <label className="vc-upload-modal__label">
              日本語 <span className="vc-upload-modal__req">*</span>
            </label>
            <input
              type="text"
              className="vc-upload-modal__input"
              value={form.nameJa}
              onChange={(e) => update('nameJa', e.target.value)}
              placeholder="例：住民票（全部事項）"
              autoComplete="off"
              disabled={submitting}
            />
          </div>
          <div className="vc-upload-modal__field">
            <label className="vc-upload-modal__label">English</label>
            <input
              type="text"
              className="vc-upload-modal__input"
              value={form.nameEn}
              onChange={(e) => update('nameEn', e.target.value)}
              placeholder="例：Certificate of residence"
              autoComplete="off"
              disabled={submitting}
            />
          </div>
          <div className="vc-upload-modal__field">
            <label className="vc-upload-modal__label">Tiếng Việt</label>
            <input
              type="text"
              className="vc-upload-modal__input"
              value={form.nameVi}
              onChange={(e) => update('nameVi', e.target.value)}
              placeholder="例：Giấy chứng nhận cư trú"
              autoComplete="off"
              disabled={submitting}
            />
          </div>

          <p className="vc-upload-modal__section-title">説明・指示（従業員向けチャット等に表示）</p>
          <div className="vc-upload-modal__field">
            <label className="vc-upload-modal__label">日本語</label>
            <textarea
              className="vc-upload-modal__textarea"
              value={form.descriptionJa}
              onChange={(e) => update('descriptionJa', e.target.value)}
              placeholder="提出時の注意や撮影範囲など"
              rows={3}
              disabled={submitting}
            />
          </div>
          <div className="vc-upload-modal__field">
            <label className="vc-upload-modal__label">English</label>
            <textarea
              className="vc-upload-modal__textarea"
              value={form.descriptionEn}
              onChange={(e) => update('descriptionEn', e.target.value)}
              placeholder="Instructions for employees"
              rows={3}
              disabled={submitting}
            />
          </div>
          <div className="vc-upload-modal__field">
            <label className="vc-upload-modal__label">Tiếng Việt</label>
            <textarea
              className="vc-upload-modal__textarea"
              value={form.descriptionVi}
              onChange={(e) => update('descriptionVi', e.target.value)}
              placeholder="Hướng dẫn cho nhân viên"
              rows={3}
              disabled={submitting}
            />
          </div>
        </div>

        <div className="vc-upload-modal__footer">
          {mode === 'edit' && onDelete && (
            <div className="vc-upload-modal__delete-wrap">
              {!showDeleteConfirm ? (
                <button type="button" className="vc-upload-modal__btn-delete" onClick={() => setShowDeleteConfirm(true)} disabled={submitting}>
                  この項目を削除
                </button>
              ) : (
                <div className="vc-upload-modal__confirm">
                  <span>削除しますか？</span>
                  <button type="button" className="vc-upload-modal__btn-yes" onClick={() => void handleConfirmDelete()} disabled={submitting}>
                    削除
                  </button>
                  <button type="button" className="vc-upload-modal__btn-no" onClick={() => setShowDeleteConfirm(false)} disabled={submitting}>
                    キャンセル
                  </button>
                </div>
              )}
            </div>
          )}
          <div className="vc-upload-modal__footer-right">
            <button type="button" className="vc-upload-modal__btn-cancel" onClick={onClose} disabled={submitting}>
              キャンセル
            </button>
            <button type="button" className="vc-upload-modal__btn-save" onClick={() => void handleSave()} disabled={!canSave}>
              {submitting ? '処理中…' : mode === 'add' ? '追加' : '保存'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const VariousCopiesUpload: React.FC = () => {
  const [rows, setRows] = useState<VariousCopyRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<ModalMode>('add');
  const [editingCode, setEditingCode] = useState<string | null>(null);

  const editingRecord = useMemo(
    () => (editingCode ? rows.find((r) => r.docCode === editingCode) ?? null : null),
    [rows, editingCode]
  );

  const modalInitial: FormState = useMemo(() => {
    if (modalMode === 'edit' && editingRecord) return recordToForm(editingRecord);
    return INITIAL_ADD_FORM;
  }, [modalMode, editingRecord]);

  const loadRows = useCallback(async () => {
    setError(null);
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/various-copies`);
      if (!res.ok) {
        throw new Error(await parseErrorMessage(res));
      }
      const data: ApiVariousCopyRow[] = await res.json();
      setRows(Array.isArray(data) ? data.map(mapApiToRecord) : []);
    } catch (e) {
      setError(e instanceof Error ? e.message : '一覧の取得に失敗しました。');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadRows();
  }, [loadRows]);

  const openAdd = useCallback(() => {
    setEditingCode(null);
    setModalMode('add');
    setModalOpen(true);
  }, []);

  const openEdit = useCallback((docCode: string) => {
    setEditingCode(docCode);
    setModalMode('edit');
    setModalOpen(true);
  }, []);

  const closeModal = useCallback(() => {
    setModalOpen(false);
    setEditingCode(null);
  }, []);

  const handleSave = useCallback(
    async (payload: FormState) => {
      const body = buildCreateBody(payload);
      if (modalMode === 'add') {
        const res = await fetch(`${API_BASE_URL}/api/various-copies`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        });
        if (!res.ok) throw new Error(await parseErrorMessage(res));
        await loadRows();
        return;
      }
      if (!editingCode) return;
      const res = await fetch(`${API_BASE_URL}/api/various-copies/${encodeURIComponent(editingCode)}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error(await parseErrorMessage(res));
      await loadRows();
    },
    [modalMode, editingCode, loadRows]
  );

  const deleteByDocCode = useCallback(
    async (docCode: string) => {
      const res = await fetch(`${API_BASE_URL}/api/various-copies/${encodeURIComponent(docCode)}`, {
        method: 'DELETE',
      });
      if (res.status === 204) {
        await loadRows();
        return;
      }
      if (!res.ok) throw new Error(await parseErrorMessage(res));
    },
    [loadRows]
  );

  const handleDelete = useCallback(async () => {
    if (!editingCode) return;
    await deleteByDocCode(editingCode);
  }, [editingCode, deleteByDocCode]);

  const handleDeleteRow = useCallback(
    async (docCode: string, displayIndex: number) => {
      if (!window.confirm(`${docCode}（番号 ${displayIndex}）を削除しますか？`)) return;
      setError(null);
      try {
        await deleteByDocCode(docCode);
      } catch (e) {
        setError(e instanceof Error ? e.message : '削除に失敗しました。');
      }
    },
    [deleteByDocCode]
  );

  const displayCell = (text: string) => (text.trim() ? text : <span className="vc-upload__cell-muted">—</span>);

  return (
    <div className="vc-upload">
      <div className="vc-upload__header-row">
        <div className="vc-upload__title-block">
          <p className="vc-upload__intro">
            標準書類（VC001–VC011）は起動時に自動登録されます。名称・説明は編集できますが、標準書類は削除できません。VC012以降は「新規追加」で登録できます。
          </p>
        </div>
        <div className="vc-upload__actions-top">
          <button type="button" className="vc-upload__btn-primary" onClick={openAdd} disabled={loading}>
            新規追加
          </button>
        </div>
      </div>

      {error && (
        <p className="vc-upload__error" role="alert">
          {error}
        </p>
      )}

      <div className="vc-upload__table-wrap">
        {loading ? (
          <p className="vc-upload__loading">読み込み中…</p>
        ) : rows.length === 0 ? (
          <p className="vc-upload__empty">登録されたアップロード書類はありません。「新規追加」から追加してください。</p>
        ) : (
          <table className="vc-upload__table">
            <thead>
              <tr>
                <th className="vc-upload__th-index">番号</th>
                <th className="vc-upload__th-code">コード</th>
                <th>書類名（日本語）</th>
                <th>書類名（English）</th>
                <th>書類名（Tiếng Việt）</th>
                <th className="vc-upload__th-actions">操作</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row, index) => {
                const n = index + 1;
                return (
                  <tr key={row.docCode}>
                    <td className="vc-upload__td-index">{n}</td>
                    <td className="vc-upload__td-code">
                      <span className="vc-upload__code">{row.docCode}</span>
                    </td>
                    <td>{displayCell(row.nameJa)}</td>
                    <td>{displayCell(row.nameEn)}</td>
                    <td>{displayCell(row.nameVi)}</td>
                    <td className="vc-upload__cell-actions">
                      <button type="button" className="vc-upload__btn-link" onClick={() => openEdit(row.docCode)}>
                        編集
                      </button>
                      {!row.builtin && (
                        <button
                          type="button"
                          className="vc-upload__btn-link vc-upload__btn-danger"
                          onClick={() => void handleDeleteRow(row.docCode, n)}
                        >
                          削除
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      <VariousCopyModal
        key={modalOpen ? `${modalMode}-${editingCode ?? 'new'}` : 'closed'}
        isOpen={modalOpen}
        mode={modalMode}
        initial={modalInitial}
        onClose={closeModal}
        onSave={async (payload) => {
          setError(null);
          try {
            await handleSave(payload);
          } catch (e) {
            const msg = e instanceof Error ? e.message : '保存に失敗しました。';
            setError(msg);
            throw e;
          }
        }}
        onDelete={
          modalMode === 'edit' && editingRecord && !editingRecord.builtin
            ? async () => {
                setError(null);
                try {
                  await handleDelete();
                } catch (e) {
                  const msg = e instanceof Error ? e.message : '削除に失敗しました。';
                  setError(msg);
                  throw e;
                }
              }
            : undefined
        }
      />
    </div>
  );
};

export default VariousCopiesUpload;
